/**
 * App navigation (Luka's APPNAV): Početna · Akustične · Vođene · Dnevnik · Omiljene · Pretplata · Profil.
 *
 * The bottom nav shows every item; the desktop header links to some of them.
 * Pendulum and History live under Profil, so their routes mark the Profil tab active.
 */
import { AudioWaveform, Headphones, Heart, House, Leaf, NotebookPen, User, type LucideIcon } from 'lucide-react';
import type { BottomNavTranslationKey } from '@/utils/translations';
import { ROUTES } from './routes';

export type NavItemId = 'home' | 'acoustic' | 'guided' | 'journal' | 'favorites' | 'subscription' | 'profile';

export interface NavItem {
  id: NavItemId;
  path: string;
  icon: LucideIcon;
  /** Visible label (short enough for 7 tabs at 320 px). */
  labelKey: BottomNavTranslationKey;
  /** Accessible name; contains the visible label. */
  ariaLabelKey: BottomNavTranslationKey;
  /** Other route prefixes that belong to this tab. */
  alsoActiveOn: readonly string[];
}

const NAV_ITEMS: readonly NavItem[] = [
  { id: 'home', path: ROUTES.home, icon: House, labelKey: 'home', ariaLabelKey: 'home', alsoActiveOn: [] },
  {
    id: 'acoustic',
    path: ROUTES.acoustic,
    icon: AudioWaveform,
    labelKey: 'acoustic',
    ariaLabelKey: 'acousticSessions',
    alsoActiveOn: [],
  },
  { id: 'guided', path: ROUTES.guided, icon: Headphones, labelKey: 'guided', ariaLabelKey: 'guidedSessions', alsoActiveOn: [] },
  { id: 'journal', path: ROUTES.journal, icon: NotebookPen, labelKey: 'journal', ariaLabelKey: 'journal', alsoActiveOn: [] },
  { id: 'favorites', path: ROUTES.favorites, icon: Heart, labelKey: 'favorites', ariaLabelKey: 'favorites', alsoActiveOn: [] },
  {
    id: 'subscription',
    path: ROUTES.subscription,
    icon: Leaf,
    labelKey: 'subscription',
    ariaLabelKey: 'subscription',
    alsoActiveOn: [ROUTES.paymentSuccess],
  },
  {
    id: 'profile',
    path: ROUTES.profile,
    icon: User,
    labelKey: 'profile',
    ariaLabelKey: 'profile',
    alsoActiveOn: [ROUTES.pendulum, ROUTES.history],
  },
];

export function getNavItems({ acoustic }: { acoustic: boolean }): NavItem[] {
  return NAV_ITEMS.filter((item) => acoustic || item.id !== 'acoustic');
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.path === ROUTES.home) return pathname === ROUTES.home;
  return [item.path, ...item.alsoActiveOn].some((prefix) => matchesPrefix(pathname, prefix));
}
